import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Seconds before expiry at which we proactively refresh the token.
// Set to 5 minutes to account for clock drift between the frontend and
// backend, and to ensure the refresh call completes before the token expires.
const PROACTIVE_REFRESH_BUFFER_SECONDS = 300;

// Shared refresh promise so concurrent requests coalesce into a single refresh call
let refreshPromise: Promise<void> | null = null;

// Request interceptor — attach Bearer token when available, proactively refresh if near expiry
apiClient.interceptors.request.use(
  async (config) => {
    const { accessToken, tokenExpiresAt, refreshToken } = useAuthStore.getState();

    // Proactively refresh if we have a token that will expire within the buffer window
    if (accessToken && tokenExpiresAt !== null && refreshToken !== null) {
      const secondsUntilExpiry = tokenExpiresAt - Math.floor(Date.now() / 1000);
      if (secondsUntilExpiry < PROACTIVE_REFRESH_BUFFER_SECONDS) {
        console.log(`[api] proactive refresh triggered, token expires in ${secondsUntilExpiry}s`);
        try {
          if (!refreshPromise) {
            refreshPromise = useAuthStore
              .getState()
              .refreshTokens()
              .finally(() => {
                refreshPromise = null;
              });
          }
          await refreshPromise;
        } catch {
          // Proactive refresh failed — let the request proceed; the response
          // interceptor will handle the resulting 401 (or force logout).
        }
      }
    }

    const { accessToken: currentToken } = useAuthStore.getState();
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — transparent token refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      try {
        // Coalesce concurrent refresh attempts into a single call
        if (!refreshPromise) {
          refreshPromise = useAuthStore
            .getState()
            .refreshTokens()
            .finally(() => {
              refreshPromise = null;
            });
        }
        await refreshPromise;

        const { accessToken } = useAuthStore.getState();
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch {
        await useAuthStore.getState().logout();
        return Promise.reject(error);
      }
    }

    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network Error:', error.message);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Wraps a mutating API call with conflict-retry logic.
 *
 * When the booking-service returns HTTP 409 (optimistic-lock conflict) it means
 * another request modified the resource concurrently.  The right fix is to:
 *   1. Re-fetch the latest version of the resource.
 *   2. Re-apply the caller's intended change on top of the fresh copy.
 *   3. Retry the mutation — up to `maxRetries` times.
 *
 * @param mutation   - Async function that performs the POST/PUT/PATCH call.
 *                     It receives the latest server state so it can rebuild its
 *                     payload if needed (e.g. for merge strategies).
 * @param refetch    - Async function that GETs the current server state.
 * @param maxRetries - Maximum number of retry attempts (default: 3).
 */
export async function withConflictRetry<TResult, TResource>(
  mutation: (latest: TResource | null) => Promise<TResult>,
  refetch: () => Promise<TResource>,
  maxRetries = 3
): Promise<TResult> {
  let attempt = 0;
  let latest: TResource | null = null;

  while (attempt <= maxRetries) {
    try {
      return await mutation(latest);
    } catch (err) {
      const status = (err as AxiosError)?.response?.status;
      if (status === 409 && attempt < maxRetries) {
        attempt++;
        console.warn(`409 Conflict on attempt ${attempt}/${maxRetries} — refetching and retrying…`);
        latest = await refetch();
      } else {
        throw err;
      }
    }
  }

  // TypeScript requires an explicit return; the loop always returns or throws.
  throw new Error('withConflictRetry: exceeded max retries');
}

